<template>
  <div>
    <b-row class="justify-content-center">
      <div class="row justify-center col-md-12">
        <div class="col-md-12">

          <!-- Generic Info Rows -->
          <div
              v-for="(item, index) in items"
              :key="index"
              v-if="item && item.visible !== false"
              class="d-flex col-md-12 mb-1 mt-1"
          >
            <!-- Label -->
            <div class="col-md-6 aling-right text-right">
              <h4 class="font-1rem margin-top-2px">
                {{ $t(item.text) }}
              </h4>
            </div>

            <!-- Tooltip -->
            <div class="text-center">
              <b-avatar size="23" variant="light-secondary">
                <feather-icon :id="`tooltip-${index}`" icon="HelpCircleIcon" size="22" class="cursor-pointer" />
              </b-avatar>
              <b-tooltip :target="`tooltip-${index}`" placement="top" custom-class="custom-tooltip">
                {{ $t(item.text + '_tooltip') }}
              </b-tooltip>
            </div>

            <!-- Dynamic Content -->
            <div v-if="item.progress" class="col-md-2 text-left d-flex align-items-center">
              <span class="font-1rem font-weight-bold">{{ funding_status }}</span>
              <span class="mr-1 font-1rem font-weight-bold">%</span>
              <b-progress style="width: 100% !important" :value="funding_status" max="100" variant="success" height="20px" />
            </div>

            <div v-else class="col-md-5 text-left">
              <h4 class="font-1rem margin-top-2px">{{ item.des }}</h4>
            </div>
          </div>

          <!-- Slider -->
          <div>
            <div class="d-flex col-md-12 mb-1 mt-3">
              <div class="col-md-12 row justify-content-center text-align-center text-center pb-1">
                <div class="col-xl-10 col-lg-10 col-md-12 p-0 m-0">
                  <b v-if="availablePercent != 100">
                    {{ $t('available_for_investment', { percent: availablePercent }) }}
                  </b>
                  <b v-else-if="availablePercent == 100">
                    {{ $t('available_for_whole') }}
                  </b>
                  <div class="slider-inner" :class="availablePercent != 100 ? 'slider-takeover' : ''">
                    <vue-slider
                        id="slider-buttons"
                        ref="slider"
                        class="vue-slider-success"
                        :direction="'ltr'"
                        :tooltip="'none'"
                        :lazy="true"
                        :adsorb="false"
                        :marks="true"
                        height="20px"
                        :data="sliderData"
                        v-model="slider"
                        @change="onSliderChange"
                        @drag-end="onDragEnd"
                    />
                    <div v-if="availablePercent !== 100">
                      <feather-icon id="hundred-percent-icon" icon="AlertTriangleIcon" size="22" />
                      <b-tooltip target="hundred-percent-icon" placement="top" custom-class="custom-tooltip">
                        {{ $t("purchase_100_replace_investors") }}
                      </b-tooltip>
                    </div>
                  </div>
                </div>
                <div v-if="selectedDisplayValue > 0 || isTakeover" class="col-md-12 mt-5">
                  <div class="col-12 text-center">
                    <b-button
                        id="buyInvoice"
                        size="lg"
                        class="invest-now-btn"
                        :variant="isTakeover ? 'warning' : 'primary'"
                        :disabled="disableButton || (selectedDisplayValue === 0 && !isTakeover)"
                        @click="openConfirmModal"
                    >
                      <div v-if="loader" class="row justify-content-center">
                        <img width="50" height="50" src="/new-loader.svg">
                      </div>
                      <div v-else>
                        <feather-icon :icon="isTakeover ? 'ZapIcon' : 'ArrowRightCircleIcon'" size="18" class="mr-50" />
                        {{ $t('invest_now') }}
                      </div>
                    </b-button>

                    <h4 class="mt-2">
                      <span v-if="isTakeover">{{ $t('takeover_purchase') }}</span>
                      <span v-else-if="availablePercent === 100 && selectedDisplayValue === 100">{{ $t('buy_the_whole') }}</span>
                      <span v-else>{{ $t('partial_purchase_description' , { percent: selectedDisplayValue }) }}</span>
                    </h4>

                    <b-modal id="bv-modal-example" centered hide-footer :title="$t('buy_definitely')">
                      <b-card-text class="text-left">
                        <b-form-checkbox
                            v-model="agreeChecked"
                            class="mb-2"
                            :checked="true"
                        >
                          <span class="font-1rem">{{ $t("agree_with_purchase_of_the_invoice") }} </span>
                          <strong>
                            <a class="font-1rem" @click="toTerms()">{{ $t("agree_with_purchase_of_the_invoice_link") }}</a>
                          </strong>
                        </b-form-checkbox>

                        <div class="mt-2 text-danger" v-if="showBottomModalText">
                          {{ $t('manual_purchase_warning') }}
                        </div>
                      </b-card-text>

                      <b-row>
                        <b-col class="text-center">
                          <b-button variant="danger" class="mr-2" @click="$bvModal.hide('bv-modal-example')">
                            {{ $t("no") }}
                          </b-button>
                          <b-button variant="primary" :disabled="!agreeChecked" @click="collectRequestData()">
                            {{ $t("yes") }}
                          </b-button>
                        </b-col>
                      </b-row>
                    </b-modal>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-row>
    <hr>
  </div>
</template>

<script>
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import {CurrencyAmountFormat} from '@core/utils/CurrencyAmountFormat'
import i18n from '@/libs/i18n'
import VueSlider from 'vue-slider-component'

export default {
  components: { VueSlider, AppTimeline, AppTimelineItem },
  props: ['modal', 'wizardActiveIndex', 'initialFractions', 'disableButton'],
  data() {
    return {
      current_balance: 0,
      slider: '0%',
      sliderData: [],
      availablePercent: 100,
      displayWidth: window.innerWidth,
      items: [],
      funding_status: 0,
      loader: false,
      buyFull: false,
      isTakeover: false,
      agreeChecked: true,
      showBottomModalText: false,
      details: [
        {
          text: i18n.t('buyer'),
          des: this.$store.state.auth.user.name,
          icon: 'UserIcon',
        },
      ],
    }
  },

  created() {
    this.getInvoice()
    this.checkManualPurchase()
  },

  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
      this.initSliderDataAndGradient()
    })
  },

  watch: {
    funding_status() { this.$nextTick(() => { this.updateOverlay(); }) },
    isTakeover() { this.$nextTick(() => { this.updateOverlay(); }) },
    availablePercent() { this.$nextTick(() => this.applyGradient()) },

  },

  beforeDestroy() { window.removeEventListener('resize', this.onResize) },

  computed: {
    selectedDisplayValue() {
      if (this.isTakeover) return 100
      if (!this.slider) return 0
      const n = Number(String(this.slider).replace('%',''))
      if (n === 100) return 100
      return n
    }
  },

  methods: {
    checkManualPurchase() {
      let is_admin_impersonated = localStorage.getItem('logged_in_as_admin') === '1';
      this.$http.post(`/market/invoice/${this.$route.params.id}/checkManualPurchase`, { is_admin_impersonated})
        .then(res => {
        if (res) {
          if (res.data.value) {
            this.showBottomModalText = true
          }
        }
      })
    },

    applyGradient() {
      const a = Number(this.availablePercent || 0)
      const avail = Math.max(0, Math.min(100, a))

      // find the .vue-slider-marks inside vue-slider
      const el = this.$el.querySelector('.vue-slider-marks')
      if (!el || this.availablePercent === 100) return

      el.style.background = `
        linear-gradient(
          to right,
          rgba(0,0,0,0) 0%,
          rgba(0,0,0,0) ${avail - 1}%,
          rgba(0,0,0,0.15) ${avail + 1}%,
          rgba(0,0,0,0.15) 100%
        )
      `
      el.style.borderRadius = `20px`
    },

    initSliderDataAndGradient() {
      const arr = []
      const step = 5
      for (let p = 0; p <= 100; p += step) arr.push(`${p}%`)

      if (arr[arr.length-1] !== '100%') arr.push('100%')
      this.sliderData = arr

      const avail = Math.max(0, Math.min(100, Number(this.availablePercent)))

      if (!this.sliderData.includes(this.slider)) {
        this.slider = `${avail}%`
        this.isTakeover = false
      }

      this.updateOverlay()
    },

    updateOverlay() {
      const funded = Math.max(0, Math.min(100, Number(this.funding_status || 0)))
      const selectedPercent = this.selectedDisplayValue
      const sliderProgress = this.$el.querySelector('.slider-takeover .vue-slider-process')
      const sliderDot = this.$el.querySelector('.slider-takeover .vue-slider-dot-handle')

      if (sliderProgress) {
        if (funded >= 0 && funded <= 100 && selectedPercent === 100) {
          sliderDot.style.border = '1px solid rgb(255, 152, 0)'
          sliderDot.style.boxShadow = 'rgb(255 152 0) -3px 0px 5px 1px'
          sliderProgress.style.background = 'linear-gradient(to right, #ff9800 0%, #ff9800 100%)'
        } else {
          sliderDot.style.border = ''
          sliderDot.style.boxShadow = ''
          sliderProgress.style.background = ''
        }
      }
    },

    amountFormat(amount, currency) {
      return CurrencyAmountFormat(amount, currency)
    },

    onResize() {
      this.displayWidth = window.innerWidth
      this.$nextTick(() => this.updateOverlay())
    },

    async onSliderChange() {
      await this._clampIfNeeded()
      await this.checkInvoiceBackend()
    },

    async onDragEnd() {
      await this._clampIfNeeded()
      this.$nextTick(() => { this.updateOverlay(); })
    },

    async _clampIfNeeded() {
      const raw = String(this.slider || '0%')
      const n = Number(raw.replace('%',''))
      if (n === 100 && this.availablePercent != 100) {
        this.isTakeover = true
        return
      }
      this.isTakeover = false
      if (n > this.availablePercent) {
        // snap back to availablePercent
        this.slider = `${this.availablePercent}%`
        if (this.$refs.slider && this.$refs.slider.setValue) {
          this.$refs.slider.setValue(this.slider)
        }

        this.$emit('disableBuy', this.availablePercent === 0)
      }
    },

    // backend check logic (extracted so onSliderChange can call after clamp)
    async checkInvoiceBackend() {
      const raw = String(this.slider || '0%')
      const selectedNum = Number(raw.replace('%',''))

      let absolutePercent = this.isTakeover ? 100 : Number(this.funding_status) + Number(selectedNum)

      if (selectedNum === 0 && !this.isTakeover) {
        this.$emit('disableBuy', true)
        absolutePercent = Number(this.funding_status)
      } else {
        this.$emit('disableBuy', false)
      }

      const data = { percent: absolutePercent }

      try {
        const res = await this.$http.post(`/market/invoice/${this.invoice.id}/check`, data)
        if (res.data) {
          this.items[2].des = res.data.value.investmentAmount ? this.amountFormat(res.data.value.investmentAmount, this.invoice.currency.name) : 0
          this.items[3].des = res.data.value.expectedNetReturn ? this.amountFormat(res.data.value.expectedNetReturn, this.invoice.currency.name) : 0

          localStorage.setItem('invoice_data', JSON.stringify({ investmentAmount: res.data.value.investmentAmount, currentBalance: Number(this.current_balance) }));

          this.$emit('setSlider', `${absolutePercent}%`)
          this.$emit('modalValues', res.data.value.investmentAmount, res.data.value.hiveFree, res.data.value.expectedNetReturn, res.data.value.currency)
        }
      } catch (err) {
        console.error('checkInvoice error', err)
      }
    },

    // load invoice + compute funded/available
    getInvoice() {
      this.$emit('disableBuy', true)
      let sum = 0
      this.funding_status = 0
      this.$http.post(`/market/invoice/${this.$route.params.id}/checkBalance`).then(res => {
        this.current_balance = res.data.value.balance

        this.$http.post(`/market/invoice/${this.$route.params.id}`).then(res => {
          this.invoice = res.data.value
          if (this.invoice) {
            if (this.invoice.fractions && this.invoice.fractions.length > 0) {
              this.invoice.fractions.forEach(el => { this.funding_status += Number(el.fraction); sum += Number(el.amount) })
            }

            this.remainingAmount = Number(this.invoice.scorings[0].amount_with_discount) - sum
            this.remainingAmount = this.remainingAmount.toFixed(2)

            this.availablePercent = Math.max(0, 100 - Number(this.funding_status))

            // set slider initial value to max available (friendly)
            this.slider = `${this.availablePercent}%`
            this.isTakeover = false

            // init marks + overlay
            this.initSliderDataAndGradient()

            this.$nextTick(() => { this.onSliderChange() })

            this.items = [
              { text: 'available_funds', des: this.current_balance ? this.amountFormat(this.current_balance, this.invoice.currency.name) : `${this.invoice.currency.name}0`, icon: 'HelpCircleIcon' },
              { text: 'funding_status', des: this.funding_status, icon: 'HelpCircleIcon', progress: true },
              { text: 'investment_amount', des: '0', icon: 'HelpCircleIcon' },
              { text: 'expected_net_return', des: '0', icon: 'HelpCircleIcon' },
            ]
          }
        })
      })
    },

    openConfirmModal() {
      if (this.availablePercent !== 100 && this.selectedDisplayValue === 100 ) {
        this.buyFull = true;
      }

      if (this.slider) {
        this.$bvModal.show('bv-modal-example')
      } else {
        this.$swal({
          position: 'center',
          icon: 'error',
          title: this.$t('choose_how_much_%_you_want_to_buy'),
          showConfirmButton: false,
          timer: 1500,
          customClass: { confirmButton: 'btn btn-danger' },
          buttonsStyling: false,
        })
      }
    },

    toTerms() {
      if (this.$i18n.locale == 'en') {
        window.open('https://www.aequitex.com/terms-condition', '_blank')
      } else {
        window.open('https://www.aequitex.com/de/bedingungen-und-konditionen', '_blank')
      }
    },

    collectRequestData() {
      if (!this.slider) {
        this.$swal({
          position: 'center',
          icon: 'error',
          title: this.$t('choose_how_much_%_you_want_to_buy'),
          showConfirmButton: false,
          timer: 1500
        })
        return
      }

      const invoiceData = JSON.parse(localStorage.getItem('invoice_data'))

      if (!invoiceData) return

      if (invoiceData.investmentAmount > invoiceData.currentBalance) {
        this.$swal({
          position: 'center',
          icon: 'error',
          title: this.$t('you_dont_have_enough_budget'),
          showConfirmButton: false,
          timer: 1500
        })
        return
      }

      this.loader = true
      this.disableButton = true
      this.$bvModal.hide('bv-modal-example')

      const selectedPercent = Number(this.slider.replace('%', ''))

      let requestData = this.buyFull ? { fraction: 100, buyFull: true } : { fraction: this.selectedDisplayValue }

      requestData.is_admin_impersonated = localStorage.getItem('logged_in_as_admin') === '1'

      requestData.confirmed_deactivation = this.showBottomModalText

      this.$emit('buy-invoice', requestData, selectedPercent, {
        onSuccess: () => {
          this.loader = false
          this.disableButton = false
        },
        onError: () => {
          this.loader = false
          this.disableButton = false
        }
      })
    },

  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-slider.scss";
</style>

<style>
/* base layout */
.slider-inner {
  position: relative; padding: 12px 6px;
  display: flex;
  align-items: center;
}

/* dots above overlays */
#slider-buttons .vue-slider-rail .vue-slider-dot,
#slider-buttons .vue-slider-rail .vue-slider-marks .vue-slider-mark {
  width: 20px !important;
  height: 20px !important;
  z-index: 6;
}

/* responsive adjustments */
@media (max-width: 530px) {
  #slider-buttons {
    padding: 0 !important; width: 100% !important;
  }

  #slider-buttons .vue-slider-rail .vue-slider-dot,
  #slider-buttons .vue-slider-rail .vue-slider-marks .vue-slider-mark {
    width: 2px !important;
    height: 2px !important;
  }

  #slider-buttons .vue-slider-rail .vue-slider-marks .vue-slider-mark .vue-slider-mark-label {
    display: none;
  }

}

@media (max-width: 370px) {
  #slider-buttons {
    padding: 0px !important;
    width: 100% !important;
  }

  #slider-buttons .vue-slider-rail .vue-slider-dot,
  #slider-buttons .vue-slider-rail .vue-slider-marks .vue-slider-mark {
    width: 1px !important;
    height: 1px !important;
  }

}

.font-1rem {
  font-size: 1rem;
}

.margin-top-2px {
  margin-top: 2px;
}

.slider-takeover .vue-slider-mark:last-child .vue-slider-mark-step {
  display: block;
  border: 1px solid rgb(255, 152, 0);
  background-color: rgb(255 255 255);
  box-shadow: 0 0 1px 2px rgb(255 152 0 / 50%);
  cursor: pointer;
}

.vue-slider-success.vue-slider {
  width: 100% !important;
}

#hundred-percent-icon {
  color: rgb(255, 152, 0);
  margin-left: 20px;
}

.invest-now-btn {
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 0.75rem 1.5rem;
  font-size: 16px;
  min-width: 166px;
  transition: all 0.5s ease-in-out;
}

</style>
